<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import * as echarts from "echarts/core";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import type { EChartsType } from "echarts/core";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { useThemeChange } from "@/features/Charts";

echarts.use([BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, SVGRenderer]);

const SERIES_COLORS = ["#4c6cff", "#6aae34", "#ef9f11", "#a072da", "#ee4745"];

type SeriesEntry = { label: string; value: number; id?: string };
type SeriesMap = Record<string, { name?: string; entries: SeriesEntry[] }>;

const props = defineProps<{
  type: "bar_horizontal" | "bar_vertical" | "line" | "pie";
  title: string;
  unit?: string;
  series: SeriesMap;
}>();

const containerRef = ref<HTMLDivElement | null>(null);
let chart: EChartsType | null = null;
let resizeObserver: ResizeObserver | null = null;

type ThemeColors = { textPrimary: string; textMuted: string; border: string; codeBg: string };

// Re-read whenever the chat's light/dark class flips (see useThemeChange below).
const themeColors = ref<ThemeColors | null>(null);

// `--chat-*` variables are scoped to the `.chat-theme`/`.chat-theme--dark` wrapper (ChatPanel.vue),
// not `html`, so they must be read from an element inside it — document.documentElement misses them.
function getCssVar(element: HTMLElement, name: string): string {
  return getComputedStyle(element).getPropertyValue(name).trim();
}

function readThemeColors(element: HTMLElement): ThemeColors {
  return {
    textPrimary: getCssVar(element, "--chat-text-primary") || "#1e293b",
    textMuted: getCssVar(element, "--chat-text-muted") || "#64748b",
    border: getCssVar(element, "--chat-border") || "#e3e8ee",
    codeBg: getCssVar(element, "--chat-code-bg") || "#f6f8fa",
  };
}

function buildOption(): echarts.EChartsCoreOption | null {
  if (!themeColors.value) return null;
  const theme = themeColors.value;
  const seriesKeys = Object.keys(props.series);
  const firstSeries = props.series[seriesKeys[0]];
  const labels = firstSeries?.entries.map((e) => e.label) ?? [];
  const unitSuffix = props.unit ? ` ${props.unit}` : "";

  const baseTextStyle = { color: theme.textPrimary, fontFamily: "Inter, sans-serif", fontSize: 12 };
  const axisLineStyle = { lineStyle: { color: theme.border } };
  const splitLineStyle = { lineStyle: { color: theme.border, type: "dashed" as const, opacity: 0.6 } };

  type TooltipAxisParam = { seriesName: string; name: string; value: number };

  const axisFormatter = (params: unknown) => {
    const list = params as TooltipAxisParam[];
    return list
      .map((p) => {
        const label = seriesKeys.length > 1 ? `${p.seriesName} — ${p.name}` : p.name;
        return `${label}: <strong>${p.value}${unitSuffix}</strong>`;
      })
      .join("<br/>");
  };

  const tooltipConfig = {
    backgroundColor: theme.codeBg,
    borderColor: theme.border,
    textStyle: { color: theme.textPrimary, fontSize: 12 },
    confine: true,
    // item formatter used only for chart types with trigger:"item" (pie)
    formatter: (params: unknown) => {
      const p = params as { seriesName: string; name: string; value: number };
      return `${p.name}: <strong>${p.value}${unitSuffix}</strong>`;
    },
  };

  if (props.type === "pie") {
    const entries = firstSeries?.entries ?? [];
    return {
      backgroundColor: "transparent",
      textStyle: baseTextStyle,
      tooltip: {
        ...tooltipConfig,
        formatter: (p: unknown) => {
          const pp = p as { name: string; value: number; percent: number };
          return `${pp.name}: <strong>${pp.value}${unitSuffix}</strong> (${pp.percent}%)`;
        },
      },
      legend: {
        orient: "horizontal",
        bottom: 0,
        left: "center",
        itemGap: 8,
        textStyle: { color: theme.textPrimary, fontSize: 10 },
        itemWidth: 10,
        itemHeight: 10,
      },
      series: [
        {
          type: "pie",
          radius: ["30%", "55%"],
          center: ["50%", "42%"],
          data: entries.map((e, i) => ({
            name: e.label,
            value: e.value,
            itemStyle: { color: SERIES_COLORS[i % SERIES_COLORS.length] },
          })),
          label: { show: false },
          emphasis: { label: { show: false } },
        },
      ],
    };
  }

  if (props.type === "bar_horizontal") {
    return {
      backgroundColor: "transparent",
      textStyle: baseTextStyle,
      grid: { left: 8, right: 48, top: 8, bottom: 8, containLabel: true },
      tooltip: { ...tooltipConfig, trigger: "axis" as const, formatter: axisFormatter },
      xAxis: {
        type: "value" as const,
        axisLine: axisLineStyle,
        axisTick: { show: false },
        axisLabel: { color: theme.textMuted, fontSize: 11 },
        splitLine: splitLineStyle,
      },
      yAxis: {
        type: "category" as const,
        data: labels,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: theme.textPrimary, fontSize: 11, width: 90, overflow: "truncate" as const },
      },
      series: seriesKeys.map((key, i) => ({
        name: props.series[key].name ?? key,
        type: "bar" as const,
        data: props.series[key].entries.map((e) => e.value),
        itemStyle: { color: SERIES_COLORS[i % SERIES_COLORS.length], borderRadius: [0, 3, 3, 0] },
        label: {
          show: true,
          position: "right" as const,
          color: theme.textMuted,
          fontSize: 11,
          formatter: (p: { value: number }) => `${p.value}`,
        },
        barMaxWidth: 20,
      })),
    };
  }

  if (props.type === "bar_vertical") {
    return {
      backgroundColor: "transparent",
      textStyle: baseTextStyle,
      grid: { left: 8, right: 8, top: 8, bottom: 8, containLabel: true },
      tooltip: { ...tooltipConfig, trigger: "axis" as const, formatter: axisFormatter },
      xAxis: {
        type: "category" as const,
        data: labels,
        axisLine: axisLineStyle,
        axisTick: { show: false },
        axisLabel: { color: theme.textPrimary, fontSize: 11, rotate: labels.length > 5 ? 30 : 0 },
      },
      yAxis: {
        type: "value" as const,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: theme.textMuted, fontSize: 11 },
        splitLine: splitLineStyle,
      },
      series: seriesKeys.map((key, i) => ({
        name: props.series[key].name ?? key,
        type: "bar" as const,
        data: props.series[key].entries.map((e) => e.value),
        itemStyle: { color: SERIES_COLORS[i % SERIES_COLORS.length], borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 32,
      })),
    };
  }

  // line
  return {
    backgroundColor: "transparent",
    textStyle: baseTextStyle,
    grid: { left: 8, right: 8, top: 8, bottom: 8, containLabel: true },
    tooltip: { ...tooltipConfig, trigger: "axis" as const, formatter: axisFormatter },
    xAxis: {
      type: "category" as const,
      data: labels,
      axisLine: axisLineStyle,
      axisTick: { show: false },
      axisLabel: { color: theme.textPrimary, fontSize: 11 },
    },
    yAxis: {
      type: "value" as const,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: theme.textMuted, fontSize: 11 },
      splitLine: splitLineStyle,
    },
    series: seriesKeys.map((key, i) => ({
      name: props.series[key].name ?? key,
      type: "line" as const,
      data: props.series[key].entries.map((e) => e.value),
      lineStyle: { color: SERIES_COLORS[i % SERIES_COLORS.length], width: 2 },
      itemStyle: { color: SERIES_COLORS[i % SERIES_COLORS.length] },
      smooth: true,
      symbol: "circle",
      symbolSize: 5,
    })),
  };
}

function chartHeight(): number {
  if (props.type === "pie") return 280;
  if (props.type === "line" || props.type === "bar_vertical") return 240;
  const seriesKeys = Object.keys(props.series);
  const firstKey = seriesKeys[0];
  const count = props.series[firstKey]?.entries.length ?? 0;
  const rowHeight = seriesKeys.length > 1 ? count * seriesKeys.length * 28 + 40 : count * 28 + 40;
  return Math.max(120, rowHeight);
}

const hasData = computed(() => Object.keys(props.series).length > 0);

function initChart() {
  if (!containerRef.value || !hasData.value) return;
  chart = echarts.init(containerRef.value, undefined, { renderer: "svg" });
  const option = buildOption();
  if (option) chart.setOption(option);
}

onMounted(() => {
  if (containerRef.value) themeColors.value = readThemeColors(containerRef.value);
  initChart();
  if (containerRef.value && hasData.value) {
    resizeObserver = new ResizeObserver(() => chart?.resize());
    resizeObserver.observe(containerRef.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  chart?.dispose();
});

// The `.chat-theme--dark` class lands on an ancestor a moment after html.dark flips (it's driven by
// its own reactive state), so wait a tick before re-reading the now-updated CSS variables.
useThemeChange(() => {
  if (!containerRef.value || !hasData.value) return;
  nextTick(() => {
    if (!containerRef.value) return;
    themeColors.value = readThemeColors(containerRef.value);
    const option = buildOption();
    if (option) chart?.setOption(option);
  });
});

watch(
  () => props.series,
  () => {
    const option = buildOption();
    if (option) chart?.setOption(option);
  },
  { deep: true }
);
</script>

<template>
  <div class="gap-px-4 flex flex-col">
    <p class="chat-text-main t-small-bold">
      {{ title
      }}<span
        v-if="unit"
        class="chat-text-muted t-footnote ml-px-6"
        >· {{ unit }}</span
      >
    </p>
    <div
      v-if="hasData"
      ref="containerRef"
      :style="{ height: chartHeight() + 'px' }" />
    <p
      v-else
      class="chat-text-muted t-caption">
      No chart data available
    </p>
  </div>
</template>
